import React from 'react'

import { gql, useMutation } from '@apollo/client'
import DashboardIcon from '@mui/icons-material/Dashboard'
import {
  AdminContext,
  AdminUI,
  Authenticated,
  CustomRoutes,
  defaultI18nProvider,
  Resource,
  useStore,
} from 'react-admin'
import { Navigate, Route, useLocation } from 'react-router-dom'

import 'antd/dist/antd.css'
import Auth from 'dash/auth'
import '../custom.scss'
import CustomLayout from './CustomLayout'
import Dashboard from './dashboard'

import { CoreJobs } from 'core-jobs-fe/src/core-jobs/index'
import AccountResources from 'dash/resources/account'
import AdminResources from 'dash/resources/admin'
import ServicesResources from 'dash/resources/services'
import UserResources from 'dash/resources/user'
import { ComponentWithSubheader } from 'dash/shared'
import { ComponentWithDynamicResourceHeader } from 'dash/shared/molecules/ComponentWithSubheader'
import AppState from 'shared/app_state'
import { customTheme } from './CustomLayout/theme'

const AccountScopedCustomRoute = () => {
  const location = useLocation()

  if (AppState.getCurrentAccount()?.id)
    return (
      <Navigate
        to={`${location.pathname.replace('account_scoped', `account/${AppState.getCurrentAccount().id}`)}${
          location.search ? location.search : ''
        }`}
      ></Navigate>
    )

  // user needs to login first
  AppState.setLastPage(`${location.pathname}${location.search ? location.search : ''}`)
  return <Navigate to="/"></Navigate>
}

const CREATE_FORM = gql`
  mutation createForm($accountId: ID!, $name: String!, $source: String!) {
    createForm(accountId: $accountId, name: $name, source: $source) {
      id
    }
  }
`

interface DashboardAppProps {
  dataProvider: any
}

import { usePageTracking } from '../shared/usePageTracking'

const DashboardUI: React.FC = () => {
  usePageTracking()

  const [accountId, setAccountId] = useStore('accountId', null)

  const [createForm] = useMutation(CREATE_FORM, {
    onCompleted: (result) => {
      console.log('completed', result)
    },
    onError: (error) => {
      console.log('error', error)
    },
  })
  // Get demo form content from local storage
  const demoForm = localStorage.getItem('formtopia-lp-demo-form-schema')

  const createDemoFormFromLocalStorage = () => {
    const accountId = AppState.getCurrentAccount()?.id
    localStorage.removeItem('formtopia-lp-demo-form-schema')

    const { name, code: source } = JSON.parse(demoForm)
    createForm({ variables: { accountId, name, source } })
  }

  const initAppIdStore = async () => {
    const urlAccountId = AppState.getAccountIdFromURL()

    // console.log('accountId', accountId)
    // console.log('urlAccountId', urlAccountId)
    // console.log('AppState.getCurrentAccount()?.id', AppState.getCurrentAccount()?.id)

    if (urlAccountId && urlAccountId != accountId) {
      await AppState.updateCurrentAccount({ id: urlAccountId })
      setAccountId(urlAccountId)
    } else if (!accountId && AppState.getCurrentAccount()?.id) setAccountId(AppState.getCurrentAccount()?.id)
  }

  return (
    <AdminUI dashboard={Dashboard} layout={CustomLayout} loginPage={Auth.Login} title={null}>
      {async (permissions: string[]) => {
        // This is called once (on login, or on app load if already logged in)
        const admin = permissions.includes('admin')

        await initAppIdStore()

        if (!accountId) return null
        // After the user is authenticated and has a demoForm saved in localStorage, try to create a demo form.
        if (demoForm) createDemoFormFromLocalStorage()

        return (
          <>
            {/* account resources */}
            <Resource
              key="account-user-resource"
              name={`account/${accountId}/team`}
              icon={AdminResources.User.Icon}
              options={[{ label: 'Team', menu: 'account' }]}
              // edit={AccountResources.TeamMember.Edit}
              list={AccountResources.TeamMember.List}
            />
            <Resource
              key="account-channel-resource"
              name={`account/${accountId}/channel`}
              icon={AccountResources.Channel.Icon}
              options={[{ label: 'Channels', menu: 'account' }]}
              create={AccountResources.Channel.Create}
              edit={AccountResources.Channel.Edit}
              list={AccountResources.Channel.List}
            />
            {/* <Resource
              key="account-notification-resource"
              name={`account/${accountId}/notification`}
              options={[{ label: 'Notifications', menu: 'account' }]}
              create={Notification.Create}
              edit={Notification.Edit}
              list={AccountResources.Notification.List}
            />, */}
            {/* NOTE account resource (for settings) must be listed last (here and in the resource array returned by this method) */}
            {/* otherwise nested routes under account don't work because it is a catch all for /account/${accountId} */}
            <Resource
              key="account-settings-resource"
              // name={`account/${accountId}/settings`}
              name="account"
              options={[{ label: 'Settings', menu: 'account' }]}
              icon={AccountResources.Settings.Icon}
              edit={AccountResources.Settings.Edit}
            />

            {/* user resources */}
            <Resource
              key="user-membership-resource"
              name="user/membership"
              options={[{ label: 'Accounts', menu: 'user' }]}
              // create={Notification.Create}
              // edit={Notification.Edit}
              list={UserResources.Memberships.List}
            />

            {/* service resources */}
            <Resource
              key="formtopia-form-resource"
              name={`account/${accountId}/services/form`}
              icon={ServicesResources.Formtopia.Icon}
              options={[
                {
                  // label: <ServicesResources.Formtopia.MenuLabel />,
                  label: 'Formtopia', // TODO enable custom node. currently, must be a string if show icon https://github.com/marmelab/react-admin/blob/master/packages/ra-ui-materialui/src/layout/MenuItemLink.tsx#L67
                  menu: 'services',
                },
              ]}
              create={() => (
                <ComponentWithSubheader
                  path={['Formtopia', 'Forms']}
                  label="Create New Form"
                  component={ServicesResources.Formtopia.Form.Create}
                />
              )}
              edit={() => (
                <ComponentWithSubheader
                  path={['Formtopia', 'Forms']}
                  component={() => (
                    <ServicesResources.Formtopia.Form.Edit
                      editLayoutWrapper={{
                        component: ComponentWithDynamicResourceHeader,
                        props: { recordAttrForBreadrumb: 'name' },
                      }}
                    />
                  )}
                />
              )}
              list={() => (
                <ComponentWithSubheader path={[]} label="Formtopia" component={ServicesResources.Formtopia.Form.List} />
              )}
            />

            {/* admin resources */}

            {admin && (
              <>
                <Resource
                  key="admin-service-resource"
                  name="admin/service"
                  icon={AdminResources.Service.Icon}
                  options={[{ label: 'Services', menu: 'admin' }]}
                  list={() => (
                    <ComponentWithSubheader path={['Admin']} label="Services" component={AdminResources.Service.List} />
                  )}
                  edit={AdminResources.Service.Edit}
                />
                <Resource
                  key="admin-account-resource"
                  name="admin/account"
                  icon={AdminResources.Account.Icon}
                  options={[{ label: 'Accounts', menu: 'admin' }]}
                  list={AdminResources.Account.List}
                  edit={AdminResources.Account.Edit}
                />
                <Resource
                  key="admin-setting-resource"
                  name="admin/setting"
                  // icon={AdminResources.Setting.Icon}
                  // options={[{ label: 'Settings', menu: 'admin' }]}
                  // list={AdminResources.Setting.List}
                  edit={AdminResources.Setting.Edit}
                />
                <Resource
                  key="admin-user-resource"
                  name="admin/user"
                  icon={AdminResources.User.Icon}
                  options={[{ label: 'Users', menu: 'admin' }]}
                  edit={AdminResources.User.Edit}
                  list={AdminResources.User.List}
                />
                <Resource
                  key="admin-jobs-resource"
                  name="admin/job"
                  icon={DashboardIcon}
                  options={[{ label: 'Job Queue', menu: 'admin' }]}
                  edit={() => (
                    <ComponentWithSubheader
                      path={['Admin', 'Job Queue']}
                      label="Edit"
                      component={CoreJobs.Resource.Edit}
                    />
                  )}
                  list={() => (
                    <ComponentWithSubheader path={['Admin']} label="Job Queue" component={CoreJobs.Resource.List} />
                  )}
                />
              </>
            )}

            {/* need to list all resources here so that reference inputs */}
            {/* can reference generic User instead of admin/User or account/User etc. */}
            <Resource key="account-resource" name="Account" />
            <Resource key="channel-resource" name="Channel" />
            <Resource key="entry-resource" name="Entry" />
            <Resource key="geo-resource" name="Geo" />
            <Resource key="job-resource" name="Job" />
            <Resource key="lookup-resource" name="Lookup" />
            <Resource key="membership-resource" name="Membership" />
            <Resource key="notification-resource" name="Notification" />
            <Resource key="setting-resource" name="Setting" />
            <Resource key="user-resource" name="User" />
            <Resource key="current-user-resource" name="CurrentUser" />
            <Resource key="service" name="Service" />
            <Resource key="subscription" name="Subscription" />
            <Resource key="robust-stripe-subscripition" name="StripeSubscription" />
            {/* <Resource key="version-resource" name="Version" /> */}

            <CustomRoutes>
              <Route
                key="current-user-route"
                path="/user/me"
                element={
                  <Authenticated>
                    <UserResources.Me.Edit />
                  </Authenticated>
                }
              />

              <Route
                key="account-billing-custom-route"
                path={`/account/${accountId}/billing`}
                element={
                  <Authenticated>
                    <AccountResources.Billing />
                  </Authenticated>
                }
              />
              <Route
                key="services-helpdesk-custom-route"
                path={`/account/${accountId}/services/helpdesk`}
                element={
                  <Authenticated>
                    <ComponentWithSubheader
                      path={['My Services']}
                      label="Helpdesk"
                      component={ServicesResources.Helpdesk}
                    />
                  </Authenticated>
                }
              />
              <Route
                key="admin-dashboard"
                path="/admin"
                element={
                  <Authenticated>
                    <ComponentWithSubheader path={['Admin']} label="Dashboard" component={AdminResources.Dashboard} />
                  </Authenticated>
                }
              />
              <Route key="account_scoped" path="/account_scoped/*" element={<AccountScopedCustomRoute />} />
              {/* <Route
              key="account-dash-custom-route"
              path="/account"
              render={() => (
                <Authenticated>
                  <Account.Dashboard />
                </Authenticated>
              )}
            />
            <Route
              key="formtopia-custom-route"
              path="/formtopia"
              render={() => (
                <Authenticated>
                  <ComponentWithSubheader path={['My Account']} label="Formtopia" component={FormtopiaDashboard} />
                </Authenticated>
              )}
            />
            <Route
              key="formtopia-forms-route"
              path="/formtopia/forms"
              render={() => (
                <Authenticated>
                  <ComponentWithSubheader path={['My Account', 'Formtopia']} label="Forms" component={Form.List} />
                </Authenticated>
              )}
            /> */}
            </CustomRoutes>
          </>
        )
      }}
    </AdminUI>
  )
}

const DashboardApp: React.FC<DashboardAppProps> = ({ dataProvider }) => {
  return (
    <AdminContext
      authProvider={Auth.Provider}
      dataProvider={dataProvider}
      i18nProvider={defaultI18nProvider}
      theme={customTheme}
    >
      <DashboardUI />
    </AdminContext>
  )
}

export default DashboardApp
